import { useCallback, useMemo, useState } from 'react';

import { TableCellClickProvider } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/hooks/use-cell-click';

import { useUser } from '@work4all/data';

import { LookUp } from '@work4all/models/lib/Classes/LookUp.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { TrafficLightStatus } from '@work4all/models/lib/Enums/TrafficLightStatus.enum';

import {
  OpenMaskOverride,
  useParameterizedOpenMask,
} from '../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { useStringifyTableGroupFilter } from '../../../hooks';
import { usePermissionsRights } from '../../../hooks/use-permissions-rights';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { useFileActions } from '../FileActions';
import { GridStateProvider } from '../grid/GridStateProvider';
import { ProjectPlanningDataProvider } from '../planning-columns/components/ProjectPlanningDataProvider';
import { useProjectPlanningActions } from '../ProjectPlanningActions';
import { ProjectPlanningCell } from '../ProjectPlanningCell';
import { ProjectTimeTrackingCell } from '../ProjectTimeCell';

import { ProjectTrafficLightStatusSwitcherCell } from './components/ProjectTrafficLightStatusSwitcherCell';
import { useProjectDataMutation } from './hooks/use-project-data-mutation';
import { useProjectTableAreas } from './hooks/use-project-table-areas';
import { useProjectTableSchema } from './hooks/use-project-table-schema';

type IProjectTableCustomColumns = Record<string, never>;

const defaultSort = [
  { field: 'startDateOuter', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

export function ProjectTable(props: IEntityTableV8) {
  return (
    <GridStateProvider>
      <ProjectTableInner {...props} />
    </GridStateProvider>
  );
}

function ProjectTableInner(props: IEntityTableV8) {
  const { forceRequestFields, ...rest } = props;

  const user = useUser();

  const [selectedProjectList, setSelectedProjectList] = useState<Project[]>([]);
  const [selectedUtilizationUserIds, setSelectedUtilizationUserIds] = useState(
    []
  );

  const [selectedRessourceClassCell, setSelectedRessourceClassCell] = useState<{
    project: Project;
    ressourceClass: LookUp;
  }>(null);

  const handleUtilizationClick = useCallback((userIds, project) => {
    setSelectedUtilizationUserIds(userIds);
    setSelectedProjectList([project]);
  }, []);

  const schema = useProjectTableSchema({
    projectPlanningProps: {
      selectedRessourceClassCell,
      onCellSelect: setSelectedRessourceClassCell,
      onUtilizationClick: handleUtilizationClick,
    },
  });

  const { entity: entityType } = schema;

  const [mutate] = useProjectDataMutation();
  const dataTable = useEntityDataTableV8<Project, IProjectTableCustomColumns>({
    ...rest,
    schema,
    defaultSort,
    secondarySort,
    forceRequestFields: useMemo<Project>(() => {
      if (!user.isMaster) return forceRequestFields as Project;
      return {
        ...forceRequestFields,
        projectAccessGroups: [
          {
            id: null,
          },
        ],
        childProjects: [
          {
            id: null,
          },
        ],
      } as Project;
    }, [forceRequestFields, user.isMaster]),
    rowModifiers: (value) => {
      return {
        isFaded: value.projectStatus?.closedStatus === -1,
      };
    },
    cells: {
      ProjectPlanning: ProjectPlanningCell,
      ProjectTimeTracking: ProjectTimeTrackingCell,
      TrafficLightStatus: ({
        value,
        row,
      }: {
        value: TrafficLightStatus;
        row: { original: Project };
      }) => {
        return (
          <ProjectTrafficLightStatusSwitcherCell
            value={value}
            onChange={(trafficLightStatus) => {
              mutate({ id: row.original.id, trafficLightStatus });
            }}
          />
        );
      },
    },
    onRowsSelected(rows) {
      props.onRowsSelected?.(rows);
      /**Only update if selection has changed to prevent loop */
      const newProjects = rows.map((r) => r.original as Project);
      if (JSON.stringify(newProjects) !== JSON.stringify(selectedProjectList)) {
        setDate(
          new Date(
            newProjects?.[0]?.startDateInner ||
              newProjects?.[0]?.startDateOuter ||
              new Date()
          )
        );
        setSelectedProjectList(newProjects);
      }
    },
  });

  const { fileActionDefinitions, openInNewTab } = useFileActions({
    entity: entityType,
    table: dataTable.table,
  });

  const { planCalendarActionDefinitions } = useProjectPlanningActions({
    table: dataTable.table,
  });

  const actions = useMemo(() => {
    return {
      openInNewTab: openInNewTab,
      custom: {
        left: [...fileActionDefinitions, ...planCalendarActionDefinitions],
      },
      createWidget: true,
    };
  }, [fileActionDefinitions, planCalendarActionDefinitions, openInNewTab]);

  const [date, setDate] = useState<Date>(new Date());

  const areas = useProjectTableAreas({
    date,
    selectedProjectList,
    selectedRessourceClassCell,
    selectedUtilizationUserIds,
    table: dataTable.table,
  });

  const presetFields = useStringifyTableGroupFilter<Project>({
    groupKey: 'group',
    groupIdKey: 'groupId',
    table: dataTable.table,
  });

  const onOpenMask = useParameterizedOpenMask({
    entityType: Entities.project,
    params: { create: { presetFields } },
  });

  const { canAccessErp } = usePermissionsRights();

  return (
    <ProjectPlanningDataProvider
      skip={!canAccessErp}
      projectList={dataTable.data}
    >
      <TableCellClickProvider onDateCellClick={setDate}>
        <OpenMaskOverride value={{ onOpenMask }}>
          <EntityTableV8
            {...props}
            {...dataTable}
            areas={areas}
            actions={actions}
          />
        </OpenMaskOverride>
      </TableCellClickProvider>
    </ProjectPlanningDataProvider>
  );
}
